import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flame, Plus, Search, Trash2, Wind, Wrench } from "lucide-react";
import {
  PROPERTY_SYSTEM_TYPE_LABELS,
  type PropertySystemType,
} from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Select,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { PropertyDialog } from "@/pages/Contracts/PropertyDialog";

const TYPE_OPTIONS = [
  { value: "", label: "Alle Anlagentypen" },
  ...Object.entries(PROPERTY_SYSTEM_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

const TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  heizung: Flame,
  klima: Wind,
  lueftung: Wind,
};

/**
 * Mandantenweite Objekt-/Anlagenübersicht — durchsuch- und filterbar, damit ein
 * Objekt auch dann wiedergefunden wird, wenn nicht mehr klar ist, zu welchem
 * Kunden es gehört (z. B. bei Kunden mit vielen Objekten).
 */
export function Properties() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);

  const [search, setSearch] = useState("");
  const [systemType, setSystemType] = useState("");
  const [dialog, setDialog] = useState<{
    open: boolean;
    customerId?: string | null;
    propertyId?: string | null;
  }>({
    open: false,
  });
  const [confirmRemove, setConfirmRemove] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [params, setParams] = useSearchParams();

  // Deep-Link von der globalen Schnellsuche (Cmd/Strg+K) — /objekte?open=<id>
  // (AUDIT-2026-10-01-UX.md, Punkt 3.1).
  useEffect(() => {
    const openId = params.get("open");
    if (openId) {
      setParams({}, { replace: true });
      setDialog({ open: true, propertyId: openId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = trpc.property.list.useQuery({
    search: search || undefined,
    systemType: (systemType || undefined) as PropertySystemType | undefined,
  });

  const { sorted, sort, toggle } = useSort(list.data ?? [], {
    label: (p) => p.label,
    type: (p) =>
      PROPERTY_SYSTEM_TYPE_LABELS[p.systemType as PropertySystemType] ??
      p.systemType,
    customer: (p) => p.customer.name,
    address: (p) => [p.addressCity, p.addressStreet].filter(Boolean).join(" "),
    device: (p) => [p.manufacturer, p.model].filter(Boolean).join(" "),
    contracts: (p) => p._count.contracts,
  });

  const remove = trpc.property.remove.useMutation({
    onSuccess: () => {
      utils.property.list.invalidate();
      addToast({ type: "success", title: "Objekt gelöscht" });
      setConfirmRemove(null);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Objekte"
        actions={
          <Button variant="primary" onClick={() => setDialog({ open: true })}>
            <Plus className="w-4 h-4" /> Neues Objekt
          </Button>
        }
      />

      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Objekt, Adresse, Hersteller, Seriennr., Kunde …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-56">
          <Select
            value={systemType}
            onChange={(e) => setSystemType(e.target.value)}
            options={TYPE_OPTIONS}
          />
        </div>
        {(search || systemType) && (
          <button
            className="text-xs text-text-tertiary hover:text-text-primary"
            onClick={() => {
              setSearch("");
              setSystemType("");
            }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !list.data?.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {search || systemType
              ? "Keine Treffer."
              : "Noch keine Objekte erfasst."}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortTh
                  label="Objekt"
                  sortKey="label"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Typ"
                  sortKey="type"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Kunde"
                  sortKey="customer"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Adresse"
                  sortKey="address"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Hersteller / Modell"
                  sortKey="device"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Verträge"
                  sortKey="contracts"
                  sort={sort}
                  onSort={toggle}
                  className="text-right"
                />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const Icon = TYPE_ICON[p.systemType] ?? Wrench;
                return (
                  <tr
                    key={p.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/kunden/${p.customer.id}`)}
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                        <span className="font-medium text-text-primary">
                          {p.label}
                        </span>
                      </div>
                    </td>
                    <td>
                      {p.systemType !== "sonstige" ? (
                        <Badge tone="neutral">
                          {PROPERTY_SYSTEM_TYPE_LABELS[
                            p.systemType as PropertySystemType
                          ] ?? p.systemType}
                        </Badge>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="text-text-secondary">{p.customer.name}</td>
                    <td className="text-text-secondary">
                      {[
                        p.addressStreet,
                        [p.addressZip, p.addressCity].filter(Boolean).join(" "),
                      ]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </td>
                    <td className="text-text-secondary">
                      {[p.manufacturer, p.model].filter(Boolean).join(" · ") ||
                        "—"}
                    </td>
                    <td className="text-right text-text-tertiary text-xs">
                      {p._count.contracts}
                    </td>
                    <td
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="text-text-tertiary hover:text-text-primary p-1"
                          title="Bearbeiten"
                          onClick={() =>
                            setDialog({
                              open: true,
                              customerId: p.customer.id,
                              propertyId: p.id,
                            })
                          }
                        >
                          <Wrench className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="text-text-tertiary hover:text-danger p-1"
                          title="Löschen"
                          onClick={() =>
                            setConfirmRemove({ id: p.id, label: p.label })
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <PropertyDialog
        state={dialog}
        onClose={() => setDialog({ open: false })}
      />

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(v) => !v && setConfirmRemove(null)}
        title="Objekt löschen?"
        message={
          confirmRemove
            ? `„${confirmRemove.label}" wird ausgeblendet (Soft-Delete).`
            : ""
        }
        confirmLabel="Löschen"
        danger
        onConfirm={() =>
          confirmRemove && remove.mutate({ id: confirmRemove.id })
        }
      />
    </div>
  );
}
